import { Sidebar } from 'ui/sidebar/sidebar';

import './abstract-feature-panel.css';

import 'ui/compact-header/compact-header.css';

import h from 'hyperscript';
import closeIcon from '@disy/cadenza-icons/close.svg';

import { MapProperty } from 'cadenza/map/map-property';
import { confirm } from 'cadenza/alert';

import { unByKey } from 'ol/Observable';

import { LayerProperty } from 'cadenza/map/layer-property';

import 'ui/form-validation';

import { icon } from 'cadenza/utils/icon/icon';
import { mediaMdUp } from 'cadenza/utils/media';

import i18n from './messages.properties';

export const NOTIFICATION_TYPE = {
  VALIDATOR: 'VALIDATOR',
  ATTRIBUTE: 'ATTRIBUTE',
  GEOMETRY: 'GEOMETRY'
};

const COMMON_FEATURE_PANEL_CLASS = 'd-feature-panel';
const NOTIFICATION_AREA_CLASS = 'notification-area';

export class AbstractFeaturePanel extends Sidebar {

  #map;
  #selectedLayer;
  #title;
  #isPrimaryInteractionChanging;
  #eventKeys;
  #onClose;
  _shouldConfirmPrimaryInteractionChange;
  _isGeometryChangeNotification;
  _hasCloseButton;
  _primaryButtonText;
  _cancelButtonText;

  constructor ({ map, layer, title, onClose, shouldConfirmPrimaryInteractionChange = false,
    isGeometryChangeNotification, hasCloseButton = true, primaryButtonText = i18n('save'), cancelButtonText = i18n('cancel') } = {}) {
    super({ shouldOpenOnMobile: true, shouldAutoHideOnMobile: false });
    this.#map = map;
    this.#selectedLayer = layer;
    this.#title = title;
    this.#isPrimaryInteractionChanging = false;
    this.#onClose = onClose;
    this._shouldConfirmPrimaryInteractionChange = shouldConfirmPrimaryInteractionChange;
    this._isGeometryChangeNotification = isGeometryChangeNotification;
    this._hasCloseButton = hasCloseButton;
    this._primaryButtonText = primaryButtonText;
    this._cancelButtonText = cancelButtonText;
  }

  connectedCallback () {
    super.connectedCallback();
    this.classList.add(COMMON_FEATURE_PANEL_CLASS);

    this.#eventKeys = [
      this.map.on(`change:${MapProperty.PRIMARY_INTERACTION}`, event => this.onMapPrimaryInteractionChange(event)),
      this.map.getLayers().on('remove', ({ element: removedLayer }) => this._onRemovedLayer(removedLayer))
    ];

    if (this._shouldConfirmPrimaryInteractionChange) {
      this.#eventKeys.push(this.map.on('changing', event => this._onMapPropertyChanging(event)));
    }

    const headerTitle = h('label.d-compact-header--title', this.#title);
    this.form = h('form', [
      h('header.d-compact-header', this._hasCloseButton ? [
        headerTitle,
        h('button.d-sidebar-close.button.button-icon.button-borderless', {
          type: 'button',
          title: i18n('close')
        }, icon(closeIcon))
      ] : headerTitle),
      h('.content'),
      h('footer', [
        ...(this._isGeometryChangeNotification ? [] : [ h('div', [
          h('button.button.button-primary', {
            title: this._primaryButtonText,
            type: 'button',
            variant: 'primary',
            onclick: () => {
              this.onSave(true);
            },
            ontouchstart: () => {
              this.onSave(true);
            }
          }, this._primaryButtonText),
          h('button.button.cancel', {
            title: this._cancelButtonText,
            type: 'button',
            onclick: () => {
              this.onCancel(true);
            },
            ontouchstart: () => {
              this.onCancel(true);
            }
          }, this._cancelButtonText)
        ]) ]),
        h(`.${NOTIFICATION_AREA_CLASS}`)
      ]),
      h('form-validation') ], {
      noValidate: true
    });

    this.append(this.form);

    setTimeout(() => this.#map.updateSize());

    /**
     * Removing listener from Sidebar which hides sidebar on small screens.
     */
    mediaMdUp.removeListener(this._onMediaChange);
  }

  disconnectedCallback () {
    super.disconnectedCallback();
    unByKey(this.#eventKeys);
    this.#map.updateSize();
  }

  _onMapPropertyChanging (event) {
    if (event.key === MapProperty.PRIMARY_INTERACTION) {
      this._setPrimaryInteractionIfConfirmed(event);
    }
  }

  _setPrimaryInteractionIfConfirmed (event) {
    if (!this.#isPrimaryInteractionChanging && !this.isOwnPrimaryInteraction(event.value)) {
      event.preventDefault();
      this.#isPrimaryInteractionChanging = true;
      confirm(i18n('confirm.title'), i18n('confirm.message')).then(shouldChange => {
        if (shouldChange) {
          this.map.set(MapProperty.PRIMARY_INTERACTION, event.value);
        }
      }).finally(() => {
        this.#isPrimaryInteractionChanging = false;
      });
    }
  }

  /**
   * When the user changes the primary interaction we know that he's trying to move away to other things, so we save work & close the panel.
   * There are interaction changes for which we don't want to close the panel. This method is used to discriminate between such interactions.
   *
   * @protected
   * @param interaction - the interaction under test
   * @return {boolean} true if a switch to this interaction does not try to close the panel, false otherwise
   */
  isOwnPrimaryInteraction (interaction) {
    return false;
  }

  get map () {
    return this.#map;
  }

  get selectedLayer () {
    return this.#selectedLayer;
  }

  get isMultipleFeatures () {
    return this.selectedLayer?.get(LayerProperty.SELECTED_FEATURE_IDS).length > 1;
  }

  /**
   * Sets the selected layer.
   *
   * @protected
   * @param selectedLayer
   */
  set selectedLayer (selectedLayer) {
    this.#selectedLayer = selectedLayer;
  }

  showErrorsInNotificationArea (errors = []) {
    const errorElements = errors.map(errorTxt => h('.d-alert.d-alert-error', errorTxt));
    const notificationAreaElement = this.querySelector(`.${NOTIFICATION_AREA_CLASS}`);
    notificationAreaElement.innerHTML = '';
    notificationAreaElement.append(...errorElements);
  }

  /**
   * Not calling onSave on purpose, here we don't care about displaying errors,
   * we just try to save and close the panel
   *
   * @protected
   */
  onMapPrimaryInteractionChange () { }

  /**
   * It only closes the panel. Child implementations can extend this.
   *
   * @param shouldResetInteraction - specifies whether we should reset the primaryInteraction
   * @protected
   */
  onSave (shouldResetInteraction = false) {
    this.close(shouldResetInteraction);
  }

  /**
   * It only closes the panel. Child implementations can extend this.
   *
   * @param shouldResetInteraction - specifies whether we should reset the primaryInteraction
   * @protected
   */
  onCancel (shouldResetInteraction = false) {
    this.close(shouldResetInteraction);
  }

  /**
   * This method closes the dialog, removes it from the DOM and optionally sets the primary interaction on the map to null.
   *
   * @param shouldResetInteraction - specifies whether we should set the primaryInteraction to null, yes if the user clicked
   * on the save or cancel button, false if we arrive here because the user changed the primaryInteraction, we don't want to reset it
   */
  close (shouldResetInteraction = true) {
    super.close();
    this.parentElement.removeChild(this);
    if (shouldResetInteraction) {
      // Needed so that the listener in feature-edit-geometry (where we create the draw interaction) removes the drawLayer
      this.map.set(MapProperty.PRIMARY_INTERACTION, null);
    }
    if (this.#onClose) {
      this.#onClose();
    }
  }

  _onRemovedLayer (removedLayer) {
    if (removedLayer.get(LayerProperty.ID) === this.#selectedLayer?.get(LayerProperty.ID)) {
      this.close(true);
    }
  }

}

export function isPanelAlreadyOpen () {
  return document.querySelector(`.${COMMON_FEATURE_PANEL_CLASS}`) != null;
}
